'use client';

import {
  initializeFiltersAndApplyDefaults,
  restoreFiltersFromLocalStorage,
} from '@/lib/redux/slices/filters';
import type { AppDispatch, RootState } from '@/lib/redux/store';
import type { YearsRangeInterface } from '@/types/api-types';
import {
  loadApiDefaults,
  loadUserPreferences,
  mergeFiltersWithDefaults,
  removeLegacyKeys,
  shouldRefreshFilters,
} from '@/utils/filterLocalStorage';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Routes that require filter data (canonical and localized paths)
const FILTER_ROUTE_SEGMENTS = [
  '/search',
  '/vyhladavanie',
  '/research',
  '/vyskum',
  '/chat',
  '/results',
  '/vysledky',
];

function isFilterRoute(pathname: string): boolean {
  // Match root page (home) which has SearchBar that uses filters
  // Locale root paths: /sk, /en, /sk/, /en/
  if (/^\/[a-z]{2}\/?$/.test(pathname)) return true;

  return FILTER_ROUTE_SEGMENTS.some((segment) => pathname.includes(segment));
}

const FilterInitializer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const pathname = usePathname();
  const filtersLoaded = useSelector(
    (state: RootState) => state.filterDataReducer.filtersLoaded
  );

  useEffect(() => {
    if (filtersLoaded) return;

    // Only load filters on pages that actually need them
    if (!isFilterRoute(pathname)) return;

    // Clean up legacy localStorage keys first
    removeLegacyKeys();

    // Check if we need to refresh API defaults (24h check)
    const needsRefresh = shouldRefreshFilters();

    if (needsRefresh) {
      // Need fresh API defaults - let Redux thunk handle it
      dispatch(initializeFiltersAndApplyDefaults());
    } else {
      // Use cached defaults, apply user preferences if they exist
      const apiDefaults = loadApiDefaults();
      const userPrefs = loadUserPreferences();

      if (!apiDefaults) {
        // No cached defaults - fetch from API
        dispatch(initializeFiltersAndApplyDefaults());
      } else if (userPrefs) {
        // Have both defaults and user prefs - merge and restore
        const merged = mergeFiltersWithDefaults(userPrefs, apiDefaults);

        // Convert to Redux format and restore
        const reduxFormat: {
          searchDecisions?: boolean;
          searchLegislation?: boolean;
          decisions?: Record<
            string,
            Array<{ id: number; name: string } | { id: string; name: string }>
          >;
          legislation?: {
            legislationTypes?: Array<{ id: number; name: string }>;
            yearRange?: YearsRangeInterface;
            activeOnly?: boolean;
            effectiveAsOf?: string;
          };
        } = {
          searchDecisions: merged.searchDecisions,
          searchLegislation: merged.searchLegislation,
          decisions: merged.decisions as Record<
            string,
            Array<{ id: number; name: string } | { id: string; name: string }>
          >,
          legislation: merged.legislation as {
            legislationTypes?: Array<{ id: number; name: string }>;
            yearRange?: YearsRangeInterface;
            activeOnly?: boolean;
            legislationInForceOnly?: boolean;
            effectiveAsOf?: string;
          },
        };

        dispatch(restoreFiltersFromLocalStorage(reduxFormat));
        dispatch(initializeFiltersAndApplyDefaults());
      } else {
        // Have defaults but no user prefs - just use defaults
        dispatch(initializeFiltersAndApplyDefaults());
      }
    }
  }, [filtersLoaded, pathname, dispatch]);

  return null; // This component does not render anything
};

export default FilterInitializer;
